import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { SearchInput } from "@hostingbv/lib/src/components/data/SearchInput";
import { useNavigate, useParams } from "react-router-dom";
import { faCircleNotch, faGlobe, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDomainSearch } from "@hostingbv/lib/src/domain-api";
import { config } from "../config";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";


export const DomainSearch: FC = () => {
  const { search } = useParams()
  const [domain, setDomain] = useState(search ?? "")
  const [lookups, setLookups] = useState<[string, "loading" | "available" | "taken", number | null][]>([])

  const { results, search: searchRequest, error: searchError } = useDomainSearch(config)

  const check = useCallback((domain: string) => {
    if (searchRequest) {
      const searchDomains = prepareDomainRequests(domain)
      searchRequest(searchDomains)
      setLookups(searchDomains.map((domain) => [domain, "loading", null]))
    }
  }, [searchRequest])

  useEffect(() => {
    if (results) {
      setLookups(previous => {
        return previous.map(([domain, status, price]) => {
          const lastResult = results.find(r => r.data.domain === domain)?.data
          if (lastResult === undefined || domain !== lastResult.domain) {
            return [domain, status, price]
          }
          return [domain, lastResult.available ? 'available' : 'taken', (lastResult.available ? lastResult.price?.price : lastResult.price?.transferPrice) ?? null]
        })
      })
    }
  }, [results])

  const submit = useCallback((e: FormEvent) => {
    e.preventDefault()
    check(domain)
  }, [domain])
  useEffect(() => {
    if (domain !== "") {
      check(domain)
    }
  }, [searchRequest])

  return (
    <ContentContainer size={"sm"}>
      <PageIconHeader icon={faGlobe}>
        <Breadcrumbs crumbs={[
          { label: "Domains", href: "/domains" },
        ]} currentPage={"Search domain"} />
        <PageHeader>Search domain</PageHeader>
      </PageIconHeader>
      <form className={"flex items-center space-x-4 mt-8"} onSubmit={submit}>
        <SearchInput value={domain} onChange={setDomain} fullWidth placeholder={"example.com"} focus />
        <Button submit size={"sm"} icon={faMagnifyingGlass} text="Check" type={"primary"} onClick={() => { }} />
      </form>
      <div className={'my-4 rounded-lg border border-slate-100 bg-white'}>
        {lookups.filter(([, status]) => searchError === null || status !== 'loading').map(([domain, status], index) => {
          const [name, tld] = explodeDomain(domain)
          return <div className={`px-4 h-16 flex items-center justify-between ${index !== 0 ? 'border-t border-slate-100' : ''}`} key={domain}>
            <span className={'font-medium'}>
              <span className={"text-slate-500"}>{name}</span>
              <span className={"text-black font-bold"}>.{tld}</span>
            </span>
            <div className={"flex items-center"}>
              {/* {price !== undefined ? <span className={"mr-4 text-sm tracking-wide text-slate-700"}>€ {price?.toFixed(2)}</span> : <></>} */}
              <SearchStatus status={status} domain={`${name}.${tld}`} />
            </div>
          </div>
        })}
      </div>
    </ContentContainer>
  );
}

const SearchStatus: FC<{ domain: string, status: "loading" | "available" | "taken" }> = (props) => {
  const navigate = useNavigate()
  if (props.status === "loading") {
    return <FontAwesomeIcon icon={faCircleNotch} spin className={"text-slate-500"} />
  } else if (props.status === "available") {
    return <Button type={"primary"} size={"xs"} text={"Register"} onClick={() => navigate(`/domains/order/${props.domain}/register`)} />
  }
  return <Button type={"secondary"} size={"xs"} text={"Transfer"} onClick={() => navigate(`/domains/order/${props.domain}/transfer`)} />
}

const searchExtensions = [
  'nl', 'com', 'eu', 'me', 'net', 'group', 'online',
]
const prepareDomainRequests = (domain: string): string[] => {
  const [name, tld] = explodeDomain(domain)
  return [
    ...(tld === "" ? [] : [`${name}.${tld}`]),
    ...searchExtensions.filter(e => e !== tld).map(e => `${name}.${e}`),
  ]
}
const explodeDomain = (domain: string): [string, string] => {
  const [name, ...tldbits] = domain.split('.')
  const tld = tldbits.join('.')
  return [name, tld]
}
